import React, { useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAdmissionsChatbot } from '../hooks/useAdmissionsChatbot';
import { renderAdmissionsBotText } from '../lib/admissionsChatRender';
import './ChatbotWidget.css';

function ChatBubbleIcon() {
  return (
    <svg width="26" height="26" viewBox="0 0 24 24" fill="none" aria-hidden>
      <path
        d="M4 5.5C4 4.12 5.12 3 6.5 3h11C18.88 3 20 4.12 20 5.5v7c0 1.38-1.12 2.5-2.5 2.5H9l-4 3v-3.5C4 14.12 4 13 4 11.5v-6Z"
        fill="currentColor"
        opacity="0.95"
      />
      <path
        d="M9.5 10.5h8c.83 0 1.5.67 1.5 1.5v4.5l-2.25-1.5H9.5c-.83 0-1.5-.67-1.5-1.5v-3Z"
        fill="currentColor"
        opacity="0.55"
      />
    </svg>
  );
}

const ChatbotWidget: React.FC = () => {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const {
    messages,
    inputText,
    setInputText,
    isLoading,
    handleSubmit,
    handleKeyPress,
    messagesEndRef
  } = useAdmissionsChatbot();

  const onClose = useCallback(() => setOpen(false), []);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (location.pathname === '/admissions-chatbot') {
    return null;
  }

  return (
    <>
      <button
        type="button"
        className="chatbot-widget-fab"
        aria-label={open ? 'Close admissions assistant' : 'Open admissions assistant'}
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        <ChatBubbleIcon />
      </button>

      {open && (
        <div
          className="chatbot-widget-panel"
          role="dialog"
          aria-modal="true"
          aria-labelledby="chatbot-widget-heading"
        >
          <div className="chatbot-widget-header">
            <h2 id="chatbot-widget-heading" className="chatbot-widget-header-title">
              Deferredly Assistant
            </h2>
            <button
              type="button"
              className="chatbot-widget-close"
              aria-label="Close"
              onClick={onClose}
            >
              ×
            </button>
          </div>

          <div className="chatbot-widget-messages">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`chatbot-widget-msg ${
                  message.isUser ? 'chatbot-widget-msg--user' : 'chatbot-widget-msg--bot'
                }`}
              >
                <div>
                  <div className="chatbot-widget-bubble">
                    {message.isUser ? message.text : renderAdmissionsBotText(message.text)}
                  </div>
                  <div className="chatbot-widget-time">
                    {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </div>
                </div>
              </div>
            ))}
            {isLoading && (
              <div className="chatbot-widget-msg chatbot-widget-msg--bot">
                <div className="chatbot-widget-bubble">
                  <div className="chatbot-widget-typing" aria-hidden>
                    <span />
                    <span />
                    <span />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form
            className="chatbot-widget-form"
            onSubmit={(e) => {
              void handleSubmit(e);
            }}
          >
            <div className="chatbot-widget-input-row">
              <textarea
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyPress}
                placeholder="Ask about MBA admissions…"
                className="chatbot-widget-textarea"
                rows={1}
                disabled={isLoading}
                aria-label="Message"
              />
              <button
                type="submit"
                className="chatbot-widget-send"
                disabled={!inputText.trim() || isLoading}
                title="Send"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden>
                  <path d="M2 21L23 12L2 3V10L17 12L2 14V21Z" fill="currentColor" />
                </svg>
              </button>
            </div>
            <div className="chatbot-widget-footer">
              <Link to="/admissions-chatbot" onClick={onClose}>
                Open full chat
              </Link>
            </div>
          </form>
        </div>
      )}
    </>
  );
};

export default ChatbotWidget;
